import { TrendingUp, Heart, Users, Mic2 } from 'lucide-react';
import { MarketWithShows } from '@shared/schema';
import { useAuth } from '@/lib/auth';
import { Link } from 'wouter';
import { BrowseFilters } from '@/modules/browse/types/browse.types';
import { filterShowsByAudienceMatch } from '@/lib/audience-matching';
import { trimShowsByFilters, type ShowFilterConfig } from '@shared/filter-engine';

interface MarketCardProps {
  market: MarketWithShows;
  isShowSaved?: (showId: number) => boolean;
  onUnsaveAllShows?: (market: MarketWithShows) => void;
  onSaveAllShows?: (market: MarketWithShows) => void;
  onSaveMarket?: (market: MarketWithShows) => void;
  isMarketSaved?: (marketId: number) => boolean;
  isAuthenticated?: boolean;
  currentFilters?: BrowseFilters;
}

export function MarketCard({ market, isShowSaved, onUnsaveAllShows, onSaveAllShows, onSaveMarket, isMarketSaved, isAuthenticated = false, currentFilters }: MarketCardProps) {
  const { user } = useAuth();

  const formatNumber = (num: number | null) => {
    if (!num) return 'N/A';
    if (num < 1000) return `${num}`;
    if (num < 1000000) return `${(num / 1000).toFixed(1)}K`;
    return `${(num / 1000000).toFixed(1)}M`;
  };

  const getImageSrc = () => {
    return market.imageUrl || "https://max.live/hubfs/inventory/markets/default-market.png";
  };

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.target as HTMLImageElement;
    if (img.src !== "https://max.live/hubfs/inventory/markets/default-market.png") {
      img.src = "https://max.live/hubfs/inventory/markets/default-market.png";
    }
  };

  const getMarketSize = (marketSize: string | null, population: number | null) => {
    // Use database marketSize if available, otherwise fallback to calculation
    if (marketSize) {
      switch (marketSize) {
        case 'small': return 'Small Market';
        case 'medium': return 'Medium Market';  
        case 'large': return 'Large Market';
        default: return 'Unknown Market';
      }
    }
    
    // Fallback calculation if marketSize is not set
    if (!population) return 'Unknown Market';
    if (population < 700000) return 'Small Market';
    if (population < 2000000) return 'Medium Market';
    return 'Large Market';
  };

  // Filter shows based on current filters using shared functions
  const upcomingShows = (() => {
    const showConfig: ShowFilterConfig = currentFilters ? {
      dateFrom: currentFilters.dateFrom,
      dateTo: currentFilters.dateTo,
      genre: currentFilters.genre,
      location: currentFilters.location,
      artistName: currentFilters.artistName,
      venueName: currentFilters.venueName,
    } : {};
    let filtered = trimShowsByFilters(market.shows, showConfig);
    if (currentFilters) {
      filtered = filterShowsByAudienceMatch(filtered, currentFilters.targetAudience, currentFilters.minMatchPercentage);
    }
    return filtered;
  })();


  return (
    <Link href={`/market/${market.id}`} onClick={(e) => {
        // Preserve filters in URL when navigating to market detail
        e.preventDefault();
        const params = new URLSearchParams(window.location.search);
        const queryString = params.toString();
        window.location.href = `/market/${market.id}${queryString ? `?${queryString}` : ''}`;
      }}>
      <div className="relative aspect-square rounded-lg overflow-hidden hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 ease-out cursor-pointer touch-manipulation active:scale-[0.98] group will-change-transform" style={{ transform: 'translateZ(0)', backfaceVisibility: 'hidden' }}>
        {/* Full-bleed market image */}
        <img
          src={getImageSrc()}
          alt={`${market.name} market`}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          onError={handleImageError}
        />

        {/* Save heart button - top right */}
        {user && onSaveMarket && isMarketSaved && (
          <button
            className={`absolute top-4 right-4 p-2 rounded-full backdrop-blur-md border transition-all duration-200 hover:scale-110 active:scale-95 z-20 group/heart ${
              isMarketSaved(market.id)
                ? 'bg-[#FA017C]/20 border-[#FA017C]/40 hover:bg-[#FA017C]/30'
                : 'bg-black/20 border-white/20 hover:bg-[#FA017C]/20'
            }`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onSaveMarket(market);
            }}
            aria-label={isMarketSaved(market.id) ? `Remove ${market.name} from saved markets` : `Save ${market.name} to saved markets`}
          >
            <Heart
              className={`w-4 h-4 transition-all duration-200 ${
                isMarketSaved(market.id)
                  ? "fill-[#FA017C] text-[#FA017C]"
                  : "text-white/80 group-hover/heart:text-[#FA017C] group-hover/heart:fill-[#FA017C]"
              }`}
            />
          </button>
        )}

        {/* Dark glassmorphism overlay for content - bottom third */}
        <div className="absolute bottom-0 left-0 right-0 bg-black/40 backdrop-blur-md border-t border-white/10 p-3 space-y-2">
          {/* Market name with show count */}
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-lg font-bold text-white truncate flex-1 min-w-0" title={market.name}>
              {market.name}
            </h3>
            <div className="flex items-center gap-1 text-white/90 flex-shrink-0">
              <Mic2 className="w-4 h-4 text-white/70" />
              <span className="text-base font-semibold">{upcomingShows.length}</span>
            </div>
          </div>

          {/* Metadata with icons and View Shows button */}
          <div className="flex items-end justify-between gap-3">
            <div className="space-y-1 flex-1 min-w-0 overflow-hidden">
              {/* Market Size */}
              <div className="flex items-center gap-1.5 text-xs text-white/90 min-w-0">
                <TrendingUp className="w-3 h-3 text-white/70 flex-shrink-0" />
                <span className="truncate">{getMarketSize(market.marketSize, market.population)}</span>
              </div>

              {/* Population */}
              <div className="flex items-center gap-1.5 text-xs text-white/90 min-w-0">
                <Users className="w-3 h-3 text-white/70 flex-shrink-0" />
                <span className="truncate">{formatNumber(market.population || 0)} population</span>
              </div>
            </div>

            {/* View Shows CTA Button */}
            <div className="flex-shrink-0">
              <div className="px-3 py-1.5 border border-[#FA017C] bg-white/10 backdrop-blur-sm text-white hover:bg-[#FA017C]/20 hover:border-[#FA017C] transition-all duration-200 text-xs rounded-md font-medium whitespace-nowrap">
                View Shows →
              </div>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}
